<script setup lang="ts">
  import { IconAccount, IconCalendarClock } from '@gat-ship/icons'
  import type { TenantUser, ISODateTimeString } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    ActionCard,
    ActionCardDescription,
    ActionCardLeadIcon,
    ActionCardTitle,
    Button,
    FormSubmit,
    DateCalendar,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useAppLoading,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import { ref, computed } from 'vue'
  import { Temporal } from '@js-temporal/polyfill'
  import { useFormatting } from '@gat-ship/utils'
  import {
    tenantUserLoadingIds,
    useTenantUserDetailContext
  } from '../../composables/useTenantUserDetail'
  import { useTenantUserUtils } from '../../composables/useTenantUserUtils'

  const { formatDateTime } = useFormatting()
  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const { TenantUserUtils } = useTenantUserUtils()
  const { setLoadingId, removeLoadingId } = useAppLoading()
  const currentTenantUser = ref<TenantUser | undefined>(undefined)

  const scheduleActivationSchema = z.object({
    userId: z.string().min(1),
    activeFrom: z.string().min(1, 'Activation date is required')
  })
  type ScheduleActivationFormValues = z.infer<typeof scheduleActivationSchema>

  const form = useForm({
    defaultValues: {
      userId: '',
      activeFrom: ''
    } as ScheduleActivationFormValues,
    validators: {
      onChange: scheduleActivationSchema
    },
    onSubmit: async ({ value }) => {
      await tenantUserProvider.commands.activateUser({
        userId: value.userId,
        activeFrom: value.activeFrom as ISODateTimeString
      })
      isOpen.value = false
    }
  })

  const isScheduled = computed(() =>
    currentTenantUser.value
      ? TenantUserUtils.isScheduledForActivation(currentTenantUser.value)
      : false
  )

  // "Cancel scheduled activation" action: opens a confirm dialog; on
  // confirm, calls the deactivate command (which clears activeFrom) and
  // closes the modal. Previously owned by a separate button component;
  // inlined here so the UX can present as an ActionCard.
  const showCancelConfirm = ref(false)
  const scheduledDateText = computed(() => {
    if (!currentTenantUser.value?.activeFrom) return ''
    return `Will activate on ${formatDateTime(currentTenantUser.value.activeFrom, 'long')}`
  })
  async function cancelActivationSchedule() {
    showCancelConfirm.value = false
    if (!currentTenantUser.value) return
    const userId = currentTenantUser.value.userId
    const loadingId = tenantUserLoadingIds.activateUser
    isOpen.value = false
    setLoadingId(loadingId)
    try {
      await tenantUserProvider.commands.deactivateUser({ userId })
    } catch {
      isOpen.value = true
    } finally {
      removeLoadingId(loadingId)
    }
  }
  function openCancelConfirm() {
    showCancelConfirm.value = true
  }

  // Bind the inline `<DateCalendar>` directly to the tanstack form
  // field. We store an ISO datetime string (local-midnight -> UTC instant)
  // on the form to match the request shape, while the calendar itself
  // speaks `Temporal.PlainDate`.
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const activeFromValue = form.useStore((s) => s.values.activeFrom)
  const activeFromErrors = form.useStore(
    (s) =>
      (s.fieldMeta?.activeFrom?.errors ?? []) as Array<
        string | { message?: string } | undefined
      >
  )

  const calendarValue = computed<Temporal.PlainDate | null>(() => {
    const v = activeFromValue.value
    if (!v) return null
    return Temporal.Instant.from(v)
      .toZonedDateTimeISO(localTimeZone)
      .toPlainDate()
  })

  const firstErrorMessage = computed(() => {
    const e = activeFromErrors.value[0]
    if (!e) return undefined
    return typeof e === 'string' ? e : (e.message ?? undefined)
  })

  function plainDateToIsoMidnight(date: Temporal.PlainDate): ISODateTimeString {
    return date
      .toZonedDateTime({
        timeZone: localTimeZone,
        plainTime: Temporal.PlainTime.from('00:00:00')
      })
      .toInstant()
      .toString() as ISODateTimeString
  }

  function onDateSelected(value: unknown) {
    // DateCalendar emits PlainDate | DateRange | null. Single mode
    // only — if a DateRange shape shows up it's a misconfiguration; bail.
    if (!value || typeof value !== 'object' || 'from' in value) {
      form.setFieldValue('activeFrom', '')
      return
    }
    form.setFieldValue(
      'activeFrom',
      plainDateToIsoMidnight(value as Temporal.PlainDate)
    )
  }

  export interface ShowScheduleActivationModalParams {
    tenantUser: TenantUser
    loadingIds?: string[]
  }

  function showModal({ tenantUser }: ShowScheduleActivationModalParams) {
    currentTenantUser.value = tenantUser
    // If the user is already scheduled, pre-fill with their existing
    // activation date. Otherwise default to tomorrow — today is in the
    // disabled range, and an inactive user's stored `activeFrom` often
    // carries a past date we don't want to surface in the calendar.
    const defaultActiveFrom = TenantUserUtils.isScheduledForActivation(
      tenantUser
    )
      ? tenantUser.activeFrom
      : plainDateToIsoMidnight(Temporal.Now.plainDateISO().add({ days: 1 }))
    form.reset({
      userId: tenantUser.userId,
      activeFrom: defaultActiveFrom
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAccount class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Schedule User Activation
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        Choose a date when this account will be activated. The user will remain
        inactive until then.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <DateCalendar
          class="w-full"
          :model-value="calendarValue"
          :disabled-ranges="[
            { from: 'epoch', to: Temporal.Now.plainDateISO() }
          ]"
          :data-testid="
            testIds.tenantUser.modal.scheduleActivation.activationDateInput
          "
          @update:model-value="onDateSelected"
        />
        <ActionCard
          v-if="currentTenantUser && isScheduled"
          @click="openCancelConfirm"
        >
          <ActionCardLeadIcon :icon="IconCalendarClock" />
          <div class="space-y-1">
            <ActionCardTitle> Cancel Activation Schedule </ActionCardTitle>
            <ActionCardDescription>
              {{ scheduledDateText }}
            </ActionCardDescription>
          </div>
        </ActionCard>
        <p v-if="firstErrorMessage" class="text-destructive text-xs">
          {{ firstErrorMessage }}
        </p>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit
        :form="form"
        label="Schedule Activation"
        :data-testid="testIds.tenantUser.modal.scheduleActivation.saveButton"
        :require-dirty="false"
      />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
  <AlertDialog v-model:open="showCancelConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle> Cancel Scheduled Activation? </AlertDialogTitle>
      </AlertDialogHeader>
      <div class="space-y-2">
        <p class="whitespace-pre-line text-sm leading-tight">
          This will remove the scheduled activation.
        </p>
        <p class="whitespace-pre-line text-sm leading-tight">
          Are you sure you want to cancel this scheduled activation?
        </p>
      </div>
      <AlertDialogFooter>
        <Button variant="outline" @click="cancelActivationSchedule()">
          Confirm
        </Button>
        <Button variant="outline" @click="showCancelConfirm = false">
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
